import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import { styled as muiStyled } from '@mui/material/styles';

import remotePointer from '../../images/remote/finger.svg';
import paintBrush from '../../images/remote/paint_brush.png';
import { MAX_REMOTE_OPPOSITE_NUM } from '../../lib/constants';
import VideoCameraOff from '../Icons/VideoCameraOff';

const AppCircularProgress = muiStyled(CircularProgress)({
  color: 'white',
});

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: auto;
`;
const ChatBox = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: ${props => (props.hidden ? 'none' : 'flex')};
  flex-direction: column;
  // justify-content: center;
  position: relative;
  // @media (max-height: 470px) {
  //   height: calc(100% - 55px);
  // }
`;
const CanvasWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 30px;
  font-weight: bold;
  position: relative;
`;
const VideBoxText = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Canvas = styled.canvas`
  display: none;
`;
const Cursor = styled.canvas`
  display: none;
  background-color: black;
`;
const Paint = styled.canvas`
  display: none;
`;
const VideoLocal = styled.video`
  display: block;
  width: 0.1px;
  height: 0.1px;
`;
const Video = styled.video`
  display: none;
`;
const Audio = styled.audio`
  display: none;
`;
const MixColorCanvas = styled.canvas`
  display: none;
  width: 240px;
  height: 240px;
`;
const DisplayNone = styled.div`
  display: none;
`;
const DummyCanvas = styled.canvas`
  display: block;
  width: 128px;
  height: 128px;
`;
const DummyImage = styled.img`
  display: none;
  width: 128px;
  height: 128px;
`;

const AudioWaveCanvas = styled.canvas`
  display: block;
`;

/**
 * ローカル（自身のデバイス）用のCanvasコンポーネントを表示する
 *
 * @returns {JSX.Element} ローカル用のCanvas要素
 */
const LocalCanvas = () => (
  <CanvasWrapper id="local">
    <div id="local_videobox">
      <label htmlFor="local_canvas" />
      <Canvas id="local_canvas" />
      <Cursor id="local_cursor" />
      <Paint id="local_paint" />
      {(() => {
        const items = [];
        for (let i = 1; i <= MAX_REMOTE_OPPOSITE_NUM; i += 1) {
          items.push(<Paint id={`local_paint${i}`} key={`local_paint_${i}`} />);
        }
        return items;
      })()}
      <VideoLocal id="local_video" autoPlay />
      <Audio id="local_audio" autoPlay controls />
    </div>
  </CanvasWrapper>
);

/**
 * リモート（通信相手）用のCanvasコンポーネントを表示する。
 *
 * @param {Object} props コンポーネントのプロパティ
 * @param {number} props.index リモートユーザーのインデックス（1〜9）
 * @returns {JSX.Element} リモート用のCanvas要素
 */
const RemoteCanvas = ({ index }) => {
  // indexは1～9まで
  const remoteId = `remote${index}`;
  return (
    <Fragment key={`${remoteId}remoteCanvas`}>
      <ChatBox id={`${remoteId}_chatBox`}>
        <CanvasWrapper id={remoteId}>
          <div>
            <label htmlFor={`${remoteId}_canvas`} />
            <Canvas id={`${remoteId}_canvas`} />
            <Cursor id={`${remoteId}_cursor`} />
            <Paint id={`${remoteId}_paint`} />
            <Video id={`${remoteId}_video`} autoPlay />
            <Audio id={`${remoteId}_audio`} autoPlay controls />
          </div>
        </CanvasWrapper>
      </ChatBox>
    </Fragment>
  );
};
RemoteCanvas.propTypes = {
  index: PropTypes.number.isRequired,
};

/**
 * ラッパーコンポーネント。ローディング表示や画像なども含む。
 *
 * @param {Object} props
 * @param {boolean} props.isWaiting ビデオやオーディオが待機中かどうかのフラグ
 * @returns {JSX.Element}
 */
const RemoteCanvasWrapper = ({ isWaiting }) => (
  <Fragment key="remote-canvas">
    <Wrapper>
      {isWaiting && (
        <VideBoxText>
          <VideoCameraOff color="white" width="100px" height="100px" />
        </VideBoxText>
      )}
      <ChatBox id="local_chatBox" hidden>
        <LocalCanvas />
      </ChatBox>
      {!isWaiting && (
        <VideBoxText>
          <AppCircularProgress size={80} />
        </VideBoxText>
      )}
      {(() => {
        const items = [];
        for (let i = 1; i <= MAX_REMOTE_OPPOSITE_NUM; i += 1) {
          items.push(<RemoteCanvas index={i} key={`remote-canvas-${i}`} />);
        }
        return items;
      })()}
    </Wrapper>
    <DisplayNone>
      <DummyImage id="pointer_img" src={remotePointer} alt="remotePointer" />
      <DummyImage id="brush_img" src={paintBrush} alt="paintBrush" />
      <MixColorCanvas id="mix_color_canvas" />
      <textarea
        id="data_transfer_data_text_area"
        rows="10"
        cols="80"
        readOnly
      />
      <AudioWaveCanvas id="audio_wave_canvas" width="320" height="240" />
      <DummyCanvas id="dummy_canvas" />
    </DisplayNone>
  </Fragment>
);

RemoteCanvasWrapper.propTypes = {
  isWaiting: PropTypes.bool.isRequired,
};

export default RemoteCanvasWrapper;
